import { ChevronDownIcon } from "@heroicons/react/24/outline"
import React, { useState } from "react"
import StepTitle from "./StepTitle"
import MagicCreditsIcon from "components/icons/MagicCredits"
import { audienceOptions, languages, useCases, voice } from "../constants"
import { capitalize } from "utils/functions"
import intl from "react-intl-universal"
import { getAiCreditsState } from "../aiCredits"
import AiCreditsNotice from "./AiCreditsNotice"

const ProgramSummaryField = ({ outLineData, uploadData, style, settings, } : { outLineData?: any, uploadData?: any, style?: any, settings?: any }) => {
  const [showDetails, setShowDetails] = useState(false)

  function removeLastUnderscoreSuffix(filename) {
    return filename.replace(/_(?!.*_)([^._]+)(\.[^.]*)$/, '$2')
  }

  const displayFilePrecision = (filePrecision) => {
    switch (filePrecision) {
      case 'high_precision':
        return intl.get('high_precision')
      case 'low_precision':
        return intl.get('magic_creative_exploration')
      default:
        return intl.get('high_precision')
    }
  }

  const displayFileUploadMethod = (uploadMethod) => {
    switch (uploadMethod) {
      case 'full_program':
        return 'Turn into full program'
      case 'single_session':
        return 'single_session'
      case 'add_as_resource':
        return 'Add as resource'
      default:
        return 'Use as inspiration'
    }
  }

  function getValueByKey(key, obj) {
    if (obj.hasOwnProperty(key)) {
        return obj[key]
    }
    return ''
}

  function getImageStatus(generateAiImages) {
    if (generateAiImages === true) {
      return intl.get('gnowbe_ai_with_generated_images')
    } else {
      return intl.get('gnowbe_ai_no_images')
    }
  }

  function getEmojiStatus(useEmoji) {
    if (useEmoji === true) {
      return intl.get('gnowbe_ai_with_generated_emojis')
    } else {
      return intl.get('gnowbe_ai_no_emojis')
    }
  }

  function getAiAudioStatus(generateAiAudio) {
    if (generateAiAudio === true) {
      return intl.get('gnowbe_ai_with_generated_ai_action')
    } else {
      return intl.get('gnowbe_ai_no_ai_action')
    }
  }


  return (
    <div className={`flex flex-row items-start justify-start sm:items-center sm:justify-between bg-[#F9F8F8] rounded-lg p-4 relative ${settings ? 'mb-0' : 'mb-5'}`}>
      <div className="w-0 sm:w-1/4 font-bold">
        <div className="absolute top-4 left-4">
          {outLineData && `${intl.get('global_content')}: (${outLineData.sessions.length - 1} ${intl.get('gnowbe_ai_sessions')})`}
          {uploadData && `${intl.get('gnowbe_ai_uploaded_files')}: (${uploadData.length} ${intl.get('gnowbe_ai_files')})`}
          {style && intl.get('gnowbe_ai_style')}
          {settings && intl.get('settings_text')}
        </div>
      </div>
      <div className="mt-10 sm:mt-0 w-auto sm:w-2/3">
        {/* outline data */}
        {outLineData && <div>
          <div>{intl.get('title')}: {outLineData.title}</div>
          {outLineData.sessions.slice(0, 4).map((session, index) => {
            if (index === 0) return null
            return <div key={index}>
              <div>{session.sessionNumber} {session.title}</div>
            </div>
          })}
          {outLineData.sessions.length > 4 && (
            <div className="transition-all duration-300 ease-in-out overflow-hidden">
              {showDetails && (
                outLineData.sessions.slice(4).map((session, index) => (
                  <div key={index + 4}>
                    <div>{session.sessionNumber} {session.title}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>}
        {/* Upload data */}
        {uploadData && <div className="flex flex-col gap-2">
          {uploadData.slice(0, 3).map((file, index) => {
            return <div key={index}>
              <div>{removeLastUnderscoreSuffix(file.displayName)} ({displayFilePrecision(file.filePrecision)}) ({displayFileUploadMethod(file.uploadMethod)})</div>
            </div>
          }
          )}
          {uploadData.length > 3 && (
            <div className="transition-all duration-300 ease-in-out overflow-hidden">
              {showDetails && (
                uploadData.slice(3).map((file, index) => (
                  <div key={index + 3}>
                    <div>{removeLastUnderscoreSuffix(file.displayName)} ({displayFilePrecision(file.filePrecision)}) ({displayFileUploadMethod(file.uploadMethod)})</div>
                  </div>
                ))
              )}
            </div>)}
        </div>}
        {/* Style data */}
        {style && <div>
          <div>{intl.get('gnowbe_ai_tone_of_voice')}: {getValueByKey(style.voice, voice) || intl.get('automatic')}</div>
          <div>{intl.get('gnowbe_ai_target_audience')}: {capitalize(style.audience === 'other' ? style.audienceOther : getValueByKey(style.audience, audienceOptions) || intl.get('automatic'))}</div>
          <div>{intl.get('magic_use_case')}: {getValueByKey(style.useCase, useCases) || intl.get('automatic')}</div>
        </div>}
        {/* Style data */}
        {settings && <div>
          <div>{intl.get('magic_language')}: {getValueByKey(settings.language, languages) || intl.get('automatic')}</div>
          <div>{getImageStatus(settings.generateAiImages)}</div>
          <div>{getEmojiStatus(settings.useEmoji)}</div>
          <div>{getAiAudioStatus(settings.generateAiAudio)}</div>
        </div>}
      </div>
      <div className="w-auto">
        {<ChevronDownIcon className={`absolute top-5 right-10 h-5 ml-2 transform transition-transform duration-300 ease-in-out ${(style || settings) || (outLineData && outLineData.sessions.length - 1 <= 3) ? 'hidden' : ''} ${showDetails ? 'rotate-180' : ''}`} onClick={() => setShowDetails(!showDetails)}/>}
      </div>
    </div>
  )
}

const ProgramFinalCosts = ({credits, organizationId, canAssignAiTasks, alreadyRequested, onAiCreditsRequested}: {credits: any, organizationId?: string, canAssignAiTasks?: boolean, alreadyRequested?: boolean, onAiCreditsRequested?: () => void}) => {
  // MagicCredits is not rendered on the summary step, so without this the
  // program flow — the main way people create with AI — would never show the
  // note explaining why a creator has nothing to spend.
  const creditsState = getAiCreditsState(credits)
  const blocked = creditsState === 'notAssigned' || creditsState === 'insufficient'

  return ( <div className="mt-6 p-5 rounded-lg bg-white">
    <StepTitle title={intl.get('gnowbe_ai_final_program_costs')} description='' />
    <div className="flex gap-2" data-test="credits-information">
      <div className="flex items-center justify-center">
        <MagicCreditsIcon className="w-10 h-10 fill-magic" />
      </div>
      <div>
        <div className={`font-bold ${blocked ? 'text-coral' : ''}`}>
          {intl.get('gnowbe_ai_total_cost')}: {credits.totalCredits}
        </div>
        <div>
          {/* "you have 0 available" reads like a bug when a creator has simply
              never been assigned any, so say that instead. */}
          {creditsState === 'notAssigned'
            ? intl.get('ai_credits_no_credits_assigned_short')
            : `${intl.get('gnowbe_ai_you_have')} ${credits.availableCredits ?? 0} ${intl.get('gnowbe_ai_credits_available')}`}
        </div>
        <AiCreditsNotice
          credits={credits}
          organizationId={organizationId}
          canAssignAiTasks={canAssignAiTasks}
          alreadyRequested={alreadyRequested}
          onAiCreditsRequested={onAiCreditsRequested} />
      </div>
    </div>
  </div>
  )
}

export { ProgramSummaryField, ProgramFinalCosts }